import { loadStripe } from '@stripe/stripe-js'
import { useModal } from '@wrisk/ui-components'
import React, { useCallback } from 'react'

import { usePrincipal } from '../application/authentication'
import {
  AdminSettleModalContent,
  AdminSettleModalFormProps,
} from '../application/organisms/admin'
import { usePolicy } from '../application/product/policy/policyContext'
import { getPolicyholder, getPolicyVersion } from '../domain'
import { TKey, useWriskTranslation } from '../infrastructure/internationalisation'
import { useApiErrorHandlingAsyncCallback } from './auth'
import { useStripeCheckoutRequest } from './stripeCheckoutRequest'

const tKey = TKey('components.pay-now')

const {
  stripe: { apiKey, stripeAccount },
} = CONFIG

interface StripeSettle {
  accountId: string
  successPath: string
  cancelPath: string
  onSuccess?: () => void
}

export const useStripeSettle = ({
  accountId,
  successPath,
  cancelPath,
  onSuccess,
}: StripeSettle) => {
  const { show } = useModal()
  const { i18n, t } = useWriskTranslation()

  const { apiClient, isAdmin } = usePrincipal()
  const { policy } = usePolicy()
  const { quote } = getPolicyVersion(policy)
  const {
    data: { dateOfBirth },
  } = getPolicyholder(quote)

  const request = useStripeCheckoutRequest({
    saveCard: false,
    successPath,
    cancelPath,
  })

  const invokeApi = useCallback(
    async (props: AdminSettleModalFormProps) =>
      await apiClient.stripeMotoSettleSession(accountId, {
        country: new Intl.Locale(i18n.language).region ?? i18n.language,
        itemName: t(tKey('line-item.name')),
        ...props,
      }),
    [accountId, apiClient, i18n.language, t],
  )

  return useApiErrorHandlingAsyncCallback(async () => {
    if (isAdmin) {
      show({
        content: (
          <AdminSettleModalContent
            invokeApi={invokeApi}
            dateOfBirth={dateOfBirth}
            onSuccess={onSuccess}
          />
        ),
      })
    } else {
      const stripe = await loadStripe(apiKey, { stripeAccount })

      await stripe?.redirectToCheckout(
        await apiClient.stripeSettleSession(accountId, request),
      )
    }
  })
}
